/**
 * CompletionModal — Shown when all onboarding steps are completed.
 *
 * Props
 * -----
 * isOpen          boolean  Whether the modal is visible
 * onClose         fn       Called when user clicks "OK" or closes modal
 * onViewPlans     fn       Called when user clicks "View Plans"
 * creditAmount    number   Credit amount earned (e.g., 5)
 * creditCurrency  string   Currency symbol (e.g., "$")
 */
import { useState, useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import styled from "styled-components";
import Confetti from "../../components/Confetti";
import completionBg from "../../assets/img/completion-modal-bg.png";
import creditIcon from "../../assets/img/icons/credit_icon_unlocked.png";

// Modal with centered positioning
const StyledModal = styled(Modal)`
  max-width: 480px;

  .modal-content {
    background: transparent;
    border: none;
    box-shadow: none;
  }
`;

// Main modal container
const ModalContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 48px;
  gap: 24px;
  
  width: 480px;
  height: 380px;
  
  background-image: url(${completionBg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 1px solid #C6B4F9;
  box-shadow: 0px 4px 50px rgba(11, 0, 34, 0.25);
  border-radius: 16px;

  @media screen and (max-width: 576px) {
    width: 100%;
    height: auto;
    padding: 32px 24px;
  }
`;

// Credit icon container
const CreditIconContainer = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  
  flex: none;
  order: 0;
  flex-grow: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  @media screen and (max-width: 576px) {
    width: 80px;
    height: 80px;
  }
`;

// Modal title
const ModalTitle = styled.h2`
  width: 384px;
  height: 38px;
  
  font-family: 'Mona Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 120%;
  text-align: center;
  
  background: linear-gradient(90deg, #2266EF 0%, #6C2EF2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  
  margin: 0;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media screen and (max-width: 576px) {
    width: 100%;
    font-size: 28px;
  }
`;

// Modal message
const ModalMessage = styled.p`
  width: 384px;
  height: 42px;
  
  font-family: 'Mona Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  
  color: #000E2A;
  margin: 0;
  
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;

  @media screen and (max-width: 576px) {
    width: 100%;
    height: auto;
    font-size: 14px;
  }
`;

// Button container
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 14px;
  
  width: 239px;
  height: 40px;
  
  flex: none;
  order: 1;
  flex-grow: 0;

  @media screen and (max-width: 576px) {
    width: auto;
    gap: 12px;
  }
`;

// OK Button (outlined)
const OkButton = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 40px;
  gap: 8px;
  
  width: 102px;
  height: 40px;
  
  background: transparent;
  border: 1px solid #2266EF;
  border-radius: 32px;
  
  font-family: 'Mona Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: #2266EF;
  
  cursor: pointer;
  transition: all 0.2s ease;
  
  flex: none;
  order: 0;
  flex-grow: 0;

  &:hover:not(:disabled) {
    background: rgba(34, 102, 239, 0.08);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  @media screen and (max-width: 576px) {
    width: auto;
    padding: 8px 24px;
  }
`;

// View Plans Button (gradient filled)
const ViewPlansButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;
  gap: 8px;
  
  width: 123px;
  height: 40px;
  
  background: linear-gradient(90deg, #2266EF 0%, #6C2EF2 100%);
  border: none;
  border-radius: 32px;
  
  font-family: 'Mona Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: #FFFFFF;
  
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(108, 46, 242, 0.25);
  
  flex: none;
  order: 1;
  flex-grow: 0;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(108, 46, 242, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(-1px);
  }

  @media screen and (max-width: 576px) {
    width: auto;
    padding: 8px 24px;
  }
`;

const CompletionModal = ({
  isOpen,
  onClose,
  onViewPlans,
  updateDiscountModalShown,
  creditAmount = 20,
  creditCurrency = "$",
  selectedOrganization,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Start confetti when modal opens
      setShowConfetti(true);
    } else {
      setShowConfetti(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setShowConfetti(false);
    if (updateDiscountModalShown) {
      updateDiscountModalShown(selectedOrganization?.id);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleViewPlans = () => {
    setShowConfetti(false);
    if (updateDiscountModalShown) {
      updateDiscountModalShown(selectedOrganization?.id);
    }
    if (onViewPlans) {
      onViewPlans();
    }
  };

  return (
    <>
      {/* Render confetti when modal is open */}
      {showConfetti && <Confetti pieceCount={250} duration={[5, 7]} />}

      <StyledModal isOpen={isOpen} toggle={handleClose} centered>
        <ModalBody style={{ padding: 0, background: 'transparent' }}>
          <ModalContainer>
            {/* Credit icon */}
            <CreditIconContainer>
              <img src={creditIcon} alt="Credit Unlocked" />
            </CreditIconContainer>

            {/* Title */}
            <ModalTitle>
              {creditCurrency}
              {creditAmount} Credit Unlocked !
            </ModalTitle>

            {/* Message */}
            <ModalMessage>
              You're all set. Your {creditCurrency}
              {creditAmount} bonus is waiting for you at checkout, use it
              whenever you're ready to upgrade your plan.
            </ModalMessage>

            {/* Action buttons */}
            <ButtonContainer>
              <OkButton onClick={handleClose}>OK</OkButton>
              <ViewPlansButton onClick={handleViewPlans}>
                View Plans
              </ViewPlansButton>
            </ButtonContainer>
          </ModalContainer>
        </ModalBody>
      </StyledModal>
    </>
  );
};

export default CompletionModal;
